import {compileTemplate, parsePathname} from '@bitstillery/mithril/router'

import {LEGACY_PATHS} from '@/lib/legacy_routes'

/** Catch-all route key; must be registered last in `routes.ts`. */
export const PORTAL_CMS_CATCH_ALL_ROUTE = '/:path...'

/**
 * Same route-path key order as `src/code/routes.ts` — `first_matching_route_path` depends on it.
 * Update when adding or reordering routes.
 */
export const PORTAL_ROUTE_PATHS_FOR_MATCH: readonly string[] = [
    // The legacy redirects, first here as in `routes.ts`, and taken from the map both files build on
    // so this list cannot fall behind it.
    ...Object.keys(LEGACY_PATHS),
    '/',
    '/redeem',
    '/invite/:code',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/my-portal/email',
    '/my-portal',
    '/my-portal/:path...',
    '/shop',
    '/shop/favourites',
    '/shop/checkout',
    '/cms/preview',
    '/cms/catalog',
    PORTAL_CMS_CATCH_ALL_ROUTE,
]

export function portal_route_paths_for_match(): string[] {
    return [...PORTAL_ROUTE_PATHS_FOR_MATCH]
}

export function first_matching_route_path(pathname: string, route_paths: string[]): string | null {
    const base = parsePathname(pathname)
    for (const route_path of route_paths) {
        const data = {path: base.path, params: {...base.params}}
        const check = compileTemplate(route_path)
        if (check(data)) {
            return route_path
        }
    }
    return null
}

export function cms_view_path_from_route_param(path_param: string | undefined): string {
    if (!path_param) {
        return '/'
    }
    const trimmed = path_param.replace(/^\/+/u, '').replace(/\/+$/u, '')
    return trimmed ? `/${trimmed}` : '/'
}

/** Normalized CMS path for the runtime API (leading slash, no trailing slash except `/`, no query/hash). */
export function cms_published_path_from_pathname(pathname: string): string {
    if (!pathname || pathname === '/') {
        return '/'
    }
    const clean = pathname.replace(/[?#].*$/su, '')
    if (!clean || clean === '/') {
        return '/'
    }
    const trimmed = clean.replace(/^\/+/u, '').replace(/\/+$/u, '')
    return trimmed ? `/${trimmed}` : '/'
}

/**
 * True when `pathname` matches a portal route that renders published CMS (`CmsDynamicPage`).
 * Uses `portal_route_paths_for_match()` when `route_paths` is omitted (same order as `routes.ts`).
 */
export function portal_path_is_published_cms_route(
    pathname: string,
    route_paths: string[] = portal_route_paths_for_match(),
): boolean {
    const matched = first_matching_route_path(pathname, route_paths)
    return (
        matched === '/' ||
        // Overlay routes (auth/redeem/invite) render the home CMS page behind their dialog.
        matched === '/login' ||
        matched === '/register' ||
        matched === '/forgot-password' ||
        matched === '/reset-password' ||
        matched === '/redeem' ||
        matched === '/invite/:code' ||
        matched === '/dashboard' ||
        matched === '/dashboard/:key' ||
        matched === PORTAL_CMS_CATCH_ALL_ROUTE
    )
}
